/**
 * This component will be used for the Jump Back In effort on the homepage.
 *
 * @module views/components/CardsRail
 * @memberof -Common
 */
import './CardsRail.scss';

import React, { useRef } from 'react';

import PropTypes from 'prop-types';

import GridContainer from '@ulta/core/components/GridContainer/GridContainer';
import Rail, { Slide } from '@ulta/core/components/Rail/Rail';
import { spreadProps } from '@ulta/core/utils/dom/dom';

import { useUuid } from '@ulta/hooks/useUuid/useUuid';

import ImagesGridCard from '../ImagesGridCard/ImagesGridCard';

/**
 * Represents a CardsRail component
 *
 * @method
 * @param {CardsRailProps} props - React properties passed from composition
 * @returns CardsRail
 */
export const CardsRail = ( props ) => {
  const cardsRailRef = useRef( null );

  const {
    dataCapture,
    modules,

    /* Rail Title */
    title = defaultProps.title,
    subTitle = defaultProps.subTitle,

    /* Rail meta data */
    railAdaMeta,
    nextAccessibility = defaultProps.nextAccessibility,
    nextClickAction,
    previousAccessibility = defaultProps.previousAccessibility,
    previousClickAction
  } = props;

  /* Setting variables */
  const titleTag = title?.htmlTag || 'h3';

  /* Sets the UUID for the rail buttons */
  const [railUuid] = useUuid();

  const _railMeta = {
    nextAccessibility: nextAccessibility,
    previousAccessibility: previousAccessibility
  };

  if( modules?.length === 0 ){
    return null;
  }

  return (
    <div
      className='CardsRail'
      ref={ cardsRailRef }
    >
      <GridContainer>
        <Rail
          railRef={ cardsRailRef }
          id={ railUuid }
          items={ modules }
          previousClickAction={ previousClickAction }
          nextClickAction={ nextClickAction }
          headline={ title?.text }
          subheadline={ subTitle?.text }
          headlineTag={ titleTag }
          dataCapture={ dataCapture }
          _railADAmeta={ railAdaMeta }
          _railMeta={ _railMeta }
          sliderPerViewXL={ 4 }
          slidesPerGroupXL={ 4 }
          sliderPerViewLG={ 4 }
          slidesPerGroupLG={ 4 }
          sliderPerViewMD={ 3 }
          slidesPerGroupMD={ 3 }
          sliderPerViewSM={ 1.5 }
          slidesPerGroupSM={ 1 }
        >
          { modules?.map( ( card, index ) => (
            <Slide key={ index }>
              <ImagesGridCard { ...spreadProps( { ...card } ) } />
            </Slide>
          ) ) }
        </Rail>
      </GridContainer>
    </div>
  );
};

/**
  * Default values for passed properties
  * @type {object}
  * @property {string} previousAccessibility='' - The default value of the previousAccessibility is empty string.
  * @property {string} nextAccessibility='' - The default value of the nextAccessibility is empty string.
  * @property {string} title='' - The default value of the title is empty string.
  * @property {string} countLabel='' - The default value of the count label is empty string.
  * @property {boolean} arrows=false - The default value for arrows is set to false.
  * @property {number} slidesToSlide=4 - The default value for slidesToSlide is set to 4.
  * @property {array} items=[] - The default value for items is set to empty array.
  */
export const defaultProps = {
  nextAccessibility: '',
  previousAccessibility: '',
  subTitle: {},
  title: {}
};

/**
 * Property type definitions
 * @typedef CardsRailProps
 * @type {object}
 * @property {array} modules - Array of modules in the rail
 * @property {string} nextAccessibility - Sets the aria label for next button.
 * @property {object} nextClickAction - The prop that holds the action for the next button.
 * @property {string} previousAccessibility - Sets the aria label for previous button.
 * @property {object} previousClickAction - The prop that holds the action for the previous button
 * @property {object} railADAMeta - Aria labels for the rail
 * @property {object} subTitle - Label for subTitle
 * @property {object} title - Label for title
 */
export const propTypes = {
  /**
   * Array of modules in the rail
   */
  modules: PropTypes.arrayOf(
    PropTypes.shape( {
      title: PropTypes.object,
      subTitle: PropTypes.string,
      images: PropTypes.arrayOf( PropTypes.object ),
      action: PropTypes.object
    } )
  ),
  /**
   * The prop that holds the aria label for next button.
   */
  nextAccessibility: PropTypes.string,
  /**
   * The prop that holds the action for the next button.
   */
  nextClickAction: PropTypes.object,
  /**
   * The prop that holds the aria label for previous button.
   */
  previousAccessibility: PropTypes.string,
  /**
   * The prop that holds the action for the previous button
   */
  previousClickAction: PropTypes.object,
  /**
   * Aria labels for the rail
   */
  railADAMeta: PropTypes.shape( {
    carouselDetailsAccessibilityLabel: PropTypes.string,
    carouselAccessibilityLabel: PropTypes.string
  } ),
  /**
   * Label for subTitle
   */
  subTitle: PropTypes.object,
  /**
   * Label for title
   */
  title: PropTypes.object
};

CardsRail.propTypes = propTypes;
CardsRail.defaultProps = defaultProps;

export default CardsRail;
